import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import parse from 'html-react-parser';
import { Calendar, User, ArrowLeft, Loader2 } from 'lucide-react';
import api, { resolveImageUrl } from '../lib/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// Rewrite <img src="/api/..."> to absolute backend URLs so images embedded in
// blog HTML remain portable across domains (preview vs production).
const parseOptions = {
  replace: (node) => {
    if (node?.type === 'tag' && node.name === 'img' && node.attribs?.src) {
      const src = node.attribs.src;
      if (src.startsWith('/api/')) {
        node.attribs.src = resolveImageUrl(src);
      }
    }
  },
};

const BlogDetailPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    setError(false);
    api
      .get(`/blog/${slug}`)
      .then((r) => {
        setPost(r.data);
        document.title = `${r.data.title} — Werdi Media Blog`;
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));

    api
      .get('/blog', { params: { limit: 4 } })
      .then((r) => setRelated(r.data.filter((p) => p.slug !== slug).slice(0, 3)))
      .catch(() => setRelated([]));
  }, [slug]);

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  const isHtml = (str = '') => /<[a-z][\s\S]*>/i.test(str);

  const stripHtml = (str = '') => str.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();

  const metaDescription = post
    ? (post.excerpt || stripHtml(post.content || '')).slice(0, 160)
    : '';
  const metaImage = post?.thumbnail ? resolveImageUrl(post.thumbnail) : '';
  const canonicalUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <div className="App bg-[#0a0a0a] min-h-screen" data-testid="blog-detail-page">
      {post && (
        <Helmet>
          <title>{`${post.title} — Werdi Media Blog`}</title>
          <meta name="description" content={metaDescription} />
          <meta name="author" content={post.author || 'Werdi Media'} />
          <meta name="keywords" content={`${post.category || ''}, Werdi Media, distribusi musik, blog musik Indonesia`} />
          <link rel="canonical" href={canonicalUrl} />
          {/* Open Graph */}
          <meta property="og:type" content="article" />
          <meta property="og:title" content={post.title} />
          <meta property="og:description" content={metaDescription} />
          <meta property="og:url" content={canonicalUrl} />
          {metaImage && <meta property="og:image" content={metaImage} />}
          <meta property="article:published_time" content={post.publish_date || ''} />
          <meta property="article:section" content={post.category || ''} />
          <meta property="article:author" content={post.author || 'Werdi Media'} />
          {/* Twitter */}
          <meta name="twitter:card" content={metaImage ? 'summary_large_image' : 'summary'} />
          <meta name="twitter:title" content={post.title} />
          <meta name="twitter:description" content={metaDescription} />
          {metaImage && <meta name="twitter:image" content={metaImage} />}
          {/* JSON-LD Article */}
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BlogPosting',
              headline: post.title,
              description: metaDescription,
              image: metaImage || undefined,
              datePublished: post.publish_date || undefined,
              dateModified: post.updated_at || post.publish_date || undefined,
              author: { '@type': 'Person', name: post.author || 'Werdi Media' },
              publisher: {
                '@type': 'Organization',
                name: 'Werdi Media',
                logo: { '@type': 'ImageObject', url: `${window.location.origin}/favicon.png` },
              },
              mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
            })}
          </script>
        </Helmet>
      )}
      <Navbar />

      {loading ? (
        <div className="flex justify-center py-40">
          <Loader2 className="w-6 h-6 text-[#E9C46A] animate-spin" />
        </div>
      ) : error || !post ? (
        <div className="pt-40 pb-24 text-center px-6">
          <p className="text-neutral-400 text-xl mb-6">Artikel tidak ditemukan.</p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D4A94F] text-black font-semibold"
          >
            <ArrowLeft className="w-4 h-4" /> Kembali ke Blog
          </Link>
        </div>
      ) : (
        <>
          <article className="pt-32 pb-16 lg:pt-40 lg:pb-20">
            <div className="max-w-4xl mx-auto px-6 lg:px-10 min-w-0">
              <Link
                to="/blog"
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-neutral-500 hover:text-[#E9C46A] link-transition mb-8"
              >
                <ArrowLeft className="w-3 h-3" /> Semua Artikel
              </Link>

              <div className="flex items-center gap-4 mb-5 flex-wrap">
                <span className="text-[10px] tracking-widest uppercase font-bold text-black bg-[#D4A94F] px-3 py-1 rounded-full">
                  {post.category}
                </span>
                <span className="text-xs text-neutral-500 flex items-center gap-1.5">
                  <Calendar className="w-3 h-3" /> {fmtDate(post.publish_date)}
                </span>
                <span className="text-xs text-neutral-500 flex items-center gap-1.5">
                  <User className="w-3 h-3" /> {post.author}
                </span>
              </div>

              <h1 className="text-white font-bold text-3xl md:text-5xl leading-[1.1] tracking-tight mb-6 break-words">
                {post.title}
              </h1>

              {post.excerpt && (
                <p className="text-neutral-300 text-lg md:text-xl leading-relaxed mb-8 break-words">
                  {post.excerpt}
                </p>
              )}

              {post.thumbnail && (
                <div className="rounded-3xl overflow-hidden border border-white/[0.06] mb-10 bg-neutral-900">
                  <img
                    src={resolveImageUrl(post.thumbnail)}
                    alt={post.title}
                    className="w-full h-auto max-h-[500px] object-cover"
                  />
                </div>
              )}

              <div className="blog-content text-neutral-200 leading-relaxed text-lg overflow-hidden" data-testid="blog-content">
                {isHtml(post.content) ? (
                  parse(post.content, parseOptions)
                ) : (
                  <div className="whitespace-pre-wrap break-words">{post.content}</div>
                )}
              </div>
            </div>
          </article>

          {related.length > 0 && (
            <section className="pb-24 border-t border-white/[0.06] pt-16">
              <div className="max-w-6xl mx-auto px-6 lg:px-10">
                <h2 className="text-white font-bold text-2xl mb-8">
                  Artikel <span className="gold-gradient-text">Lainnya</span>
                </h2>
                <div className="grid md:grid-cols-3 gap-6">
                  {related.map((p) => (
                    <Link
                      key={p.id}
                      to={`/blog/${p.slug}`}
                      className="group bg-[#0f0f0f] border border-white/[0.06] hover:border-[#D4A94F]/40 rounded-3xl overflow-hidden card-transition hover:-translate-y-1"
                    >
                      <div className="aspect-[16/10] bg-neutral-900 overflow-hidden">
                        {p.thumbnail && (
                          <img
                            src={resolveImageUrl(p.thumbnail)}
                            alt=""
                            className="w-full h-full object-cover btn-transition group-hover:scale-105"
                          />
                        )}
                      </div>
                      <div className="p-5">
                        <span className="text-[10px] tracking-widest uppercase text-[#D4A94F] font-bold">
                          {p.category}
                        </span>
                        <h3 className="text-white font-bold mt-2 group-hover:text-[#E9C46A] btn-transition">
                          {p.title}
                        </h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </>
      )}

      <Footer />
    </div>
  );
};

export default BlogDetailPage;
